import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-3">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您要查找的漫画不存在或已被移除。</p>
            <Link
              to="/category"
              className="inline-flex items-center gap-2 bg-primary-500 text-white text-sm px-5 py-2.5 rounded-full font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i> 浏览漫画列表
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryInfo = categories.find((c) => c.slug === comic.categorySlug);
  const relatedComics = comics
    .filter((c) => c.categorySlug === comic.categorySlug && c.id !== comic.id)
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-6 md:py-8">
          <nav className="mb-6" aria-label="Breadcrumb">
            <ol className="flex items-center gap-1.5 text-sm flex-wrap">
              <li>
                <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              </li>
              <li className="text-foreground-300">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <Link to="/category" className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">漫画分类</Link>
              </li>
              <li className="text-foreground-300">
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              {categoryInfo && (
                <>
                  <li>
                    <Link to={`/category/${categoryInfo.slug}`} className="text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer">
                      {categoryInfo.name}
                    </Link>
                  </li>
                  <li className="text-foreground-300">
                    <i className="ri-arrow-right-s-line text-xs"></i>
                  </li>
                </>
              )}
              <li>
                <span className="text-foreground-700 font-medium">{comic.title}</span>
              </li>
            </ol>
          </nav>

          <article className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-shrink-0 w-full lg:w-[280px]">
              <div className="relative overflow-hidden rounded-lg bg-background-100 aspect-[3/4] max-w-[220px] mx-auto lg:max-w-full">
                <img
                  src={comic.cover}
                  alt={`${comic.title} - ${comic.originalTitle}`}
                  title={`${comic.title} 韩国漫画`}
                  className="w-full h-full object-cover object-top"
                />
                {comic.status === '已完结' && (
                  <span className="absolute top-3 left-3 bg-secondary-500 text-white text-xs px-2.5 py-1 rounded-full whitespace-nowrap">
                    已完结
                  </span>
                )}
                {comic.status === '连载中' && (
                  <span className="absolute top-3 left-3 bg-accent-500 text-white text-xs px-2.5 py-1 rounded-full whitespace-nowrap">
                    连载中
                  </span>
                )}
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-1">
                {comic.title}
              </h1>
              <p className="text-sm text-foreground-500 mb-4">{comic.originalTitle}</p>

              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="flex items-center gap-1.5 bg-accent-100 text-accent-700 text-sm px-3 py-1 rounded-full">
                  <i className="ri-star-fill text-accent-500"></i>
                  <span className="font-semibold">{comic.rating}</span>
                </div>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line mr-1"></i>
                  {comic.views.toLocaleString()} 次阅读
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-book-open-line mr-1"></i>
                  共 {comic.chapters} 话
                </span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-user-line mr-1"></i>
                  {comic.author} / {comic.artist}
                </span>
              </div>

              <div className="flex flex-wrap gap-1.5 mb-5">
                {comic.tags.map((tag) => (
                  <span key={tag} className="bg-secondary-100 text-secondary-700 text-xs px-2.5 py-1 rounded-full whitespace-nowrap">
                    {tag}
                  </span>
                ))}
                {categoryInfo && (
                  <Link
                    to={`/category/${categoryInfo.slug}`}
                    className="bg-primary-100 text-primary-700 text-xs px-2.5 py-1 rounded-full whitespace-nowrap hover:bg-primary-200 transition-colors cursor-pointer"
                  >
                    {categoryInfo.name}
                  </Link>
                )}
              </div>

              <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
              </div>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-semibold text-foreground-900">章节列表</h2>
                  <div className="flex items-center gap-2">
                    <button className="text-xs text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap">
                      正序
                    </button>
                    <span className="text-foreground-200">|</span>
                    <button className="text-xs text-foreground-400 hover:text-foreground-600 font-medium transition-colors cursor-pointer whitespace-nowrap">
                      倒序
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                  {comic.chapters_list.map((chapter) => (
                    <div
                      key={chapter.num}
                      className="flex items-center justify-between bg-background-100 hover:bg-background-200 rounded-lg px-4 py-3 transition-colors cursor-pointer group"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-xs text-foreground-400 font-mono whitespace-nowrap">
                          第{chapter.num}话
                        </span>
                        <span className="text-sm text-foreground-700 truncate group-hover:text-primary-500 transition-colors">
                          {chapter.title}
                        </span>
                      </div>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-4">{chapter.date}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm px-6 py-2.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-play-circle-line"></i>
                  开始阅读第一话
                </button>
                <button className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm px-6 py-2.5 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-bookmark-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </article>
        </div>

        {relatedComics.length > 0 && (
          <section className="bg-background-100/70 py-10 md:py-14">
            <div className="max-w-[1280px] mx-auto px-4 md:px-6">
              <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
                同类型推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
                {relatedComics.map((related) => (
                  <Link
                    key={related.id}
                    to={`/article/${related.slug}`}
                    className="group block cursor-pointer"
                  >
                    <div className="relative overflow-hidden rounded-lg bg-background-100 aspect-[3/4]">
                      <img
                        src={related.cover}
                        alt={related.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="mt-2.5">
                      <h3 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-accent-500 font-semibold">{related.rating}</span>
                        <span className="text-xs text-foreground-400">共{related.chapters}话</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: `${comic.title} - 韩国漫画 - 汗汗漫画网`,
            description: comic.description,
            author: {
              '@type': 'Person',
              name: comic.author,
            },
            datePublished: '2024-01-01',
            dateModified: comic.updatedAt,
            publisher: {
              '@type': 'Organization',
              name: '汗汗漫画网',
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://hanhanmanhua.com/article/${comic.slug}`,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hanhanmanhua.com/' },
              { '@type': 'ListItem', position: 2, name: '漫画分类', item: 'https://hanhanmanhua.com/category' },
              ...(categoryInfo
                ? [{ '@type': 'ListItem', position: 3, name: categoryInfo.name, item: `https://hanhanmanhua.com/category/${categoryInfo.slug}` }]
                : []),
              { '@type': 'ListItem', position: categoryInfo ? 4 : 3, name: comic.title },
            ],
          }),
        }}
      />
    </div>
  );
}